<template>
    <Component
        :is="component"
        :to="to"
        v-bind="$attrs"
        :target="!isButton && openInNewTab ? '_blank' : null"
        :disabled="isButton ? disabled || isLoading : null"
        :type="isButton ? type : null"
        :class="[
            paddingClasses,
            {
                'inline-flex items-center justify-center': !isButton,
                'md:hover:before:opacity-100':
                    !isButton && appearance === 'light',
                'active:after:opacity-100': !isButton && appearance === 'dark',
                'relative overflow-hidden rounded-full border border-white px-3.5 text-16/120 text-white before:absolute before:inset-0 before:size-full before:bg-gradient-to-l before:from-white/50 before:to-white/20 before:opacity-0 before:transition-all before:duration-300 after:absolute after:inset-0 after:size-full after:bg-gradient-button after:opacity-0 after:transition-all after:duration-300 enabled:active:border-transparent enabled:active:after:opacity-100 md:enabled:hover:before:opacity-100 lg:text-20/120':
                    appearance === 'light',
                'relative overflow-hidden text-16/148 text-accent-100 after:absolute after:bottom-0 after:left-0 after:block after:h-0.5 after:w-full after:scale-0 after:bg-accent-100 after:transition-transform after:duration-300 md:hover:after:scale-100':
                    appearance === 'dark',
                'relative overflow-hidden rounded-full bg-card px-3.5 text-16/120 text-ash-200 before:absolute before:inset-0 before:size-full before:bg-gradient-to-l before:from-white/50 before:to-white/20 before:opacity-0 before:transition-all before:duration-300 after:absolute after:inset-0 after:size-full after:bg-gradient-button after:opacity-0 after:transition-all after:duration-300 enabled:active:border-transparent enabled:active:after:opacity-100 md:hover:before:opacity-100':
                    appearance === 'accent',
                'relative rounded-full text-16/150 font-semibold text-ash-200 hover:text-accent-100 active:text-accent-100 active:opacity-50':
                    appearance === 'ghost',

                'py-3':
                    ['light', 'accent'].includes(appearance) &&
                    size !== 'small',

                'h-11': size === 'default',
                'h-11 md:h-16': size === 'accent',
                'h-8 py-0 !text-12/100': size === 'small',

                'pr-3':
                    size === 'default' && iconRight && appearance !== 'ghost',
            },
        ]"
        class="relative transition-all duration-300 before:transform-gpu after:transform-gpu disabled:cursor-not-allowed disabled:select-none disabled:opacity-50">
        <span
            v-if="appearance !== 'ghost'"
            class="bg-noise absolute inset-0"></span>
        <div
            :class="[
                ['dark', 'ghost'].includes(appearance)
                    ? 'space-x-1'
                    : 'space-x-3',
                iconLeft === 'rutube' ? 'items-end' : 'items-center',
            ]"
            class="relative z-[1] flex justify-center">
            <AtomIcon
                v-if="iconLeft"
                :name="iconLeft"
                :class="[isLoading ? 'opacity-0' : '', iconSizeClasses]"
                class="shrink-0" />
            <span :class="{ 'opacity-0': isLoading }">
                <slot />
            </span>
            <AtomIcon
                v-if="iconRight"
                :name="iconRight"
                :class="[isLoading ? 'opacity-0' : '', iconSizeClasses]"
                class="shrink-0" />
        </div>
        <span
            v-if="isLoading"
            class="absolute inset-0 !ml-0 flex w-full items-center justify-center">
            <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                class="size-5 animate-spin">
                <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                    class="opacity-25"></circle>
                <path
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    class="opacity-75"></path>
            </svg>
        </span>
    </Component>
</template>

<script setup lang="ts">
import type { PropType } from 'vue'

import AtomIcon from '~/components/atom/atom-icon/AtomIcon.vue'
import type { TAtomIcon } from '~/components/atom/atom-icon/atom-icon.types'

import type { TBaseLinkApperance, TBaseLinkSize } from './base-link.types'

/**
 * Минимальное время отображения лоадера - 500мс,
 * чтобы при быстрых ответах от сервера кнопка не «моргала».
 */
const LOADER_MIN_DISPLAY_INTERVAL = 500

const props = defineProps({
    appearance: {
        /**
         * Тип кнопки
         */
        type: String as PropType<TBaseLinkApperance>,
        default: 'light',
    },
    size: {
        type: String as PropType<TBaseLinkSize>,
        default: 'default',
    },
    iconLeft: {
        /**
         * Иконка слева, если ничего не передается, то иконки нет
         */
        type: [String, null as unknown] as PropType<TAtomIcon | null>,
        default: null,
    },
    iconRight: {
        /**
         * Иконка справа, если ничего не передается, то иконки нет
         */
        type: [String, null as unknown] as PropType<TAtomIcon | null>,
        default: null,
    },
    disabled: {
        /**
         * Перевод кнопки в неактивную, работает только если не передается ссылка
         */
        type: Boolean as PropType<boolean>,
        default: false,
    },
    loading: {
        /**
         * Показать лоадер
         * Перевод кнопки в неактивную, работает только если не передается ссылка
         */
        type: Boolean as PropType<boolean>,
        default: false,
    },
    block: {
        /**
         * Растягивает компонент на ширину контейнера
         */
        type: Boolean as PropType<boolean>,
        default: false,
    },
    to: {
        /**
         * Выводит ссылку в виде кнопки
         */
        type: [String, null as unknown] as PropType<string | null>,
        default: null,
    },
    type: {
        /**
         * Тип кнопки
         */
        type: String as PropType<'button' | 'submit' | 'clear'>,
        default: 'button',
    },
    iconSize: {
        type: String as PropType<'medium' | 'big'>,
        default: 'medium',
    },
    openInNewTab: {
        type: Boolean as PropType<boolean>,
        default: false,
    },
})

const isLoading = ref(false)

const component = computed(() => {
    return props.to ? resolveComponent('NuxtLink') : 'button'
})

const isButton = computed(() => {
    return component.value === 'button'
})

const paddingClasses = computed(() => {
    const hasIcon = Boolean(props.iconLeft || props.iconRight)

    if (['dark', 'ghost'].includes(props.appearance)) {
        return ''
    }

    if (props.size === 'default') {
        return 'px-5'
    } else {
        return hasIcon ? 'lg:p-5' : 'lg:py-5 lg:px-7'
    }
})

const iconSizeClasses = computed(() => {
    if (['dark', 'ghost'].includes(props.appearance)) {
        return 'size-4'
    }

    return props.iconSize === 'medium' ? 'size-5' : 'size-6'
})

watchEffect(() => {
    if (!props.loading) {
        setTimeout(() => {
            isLoading.value = false
        }, LOADER_MIN_DISPLAY_INTERVAL)
    } else {
        isLoading.value = true
    }
})
</script>
